import { SelectionData } from '../../../models/util.model'
import { DEFAULT_CRON_EXPRESSION } from '../consts/activity.const'
import { CronjobSchedule } from '../models/activity.entity'
import {
  CronAmountKey,
  CronjobScheduleType,
  CronPeriod,
} from '../models/activity.types'

export function createDefaultSchedule(): CronjobSchedule {
  return {
    minutes: 'every',
    minutesAmount: [],
    hours: 'every',
    hoursAmount: [],
    days: 'every',
    daysAmount: [],
    months: 'every',
    monthsAmount: [],
    weekdays: 'every',
    weekdaysAmount: [],
  }
}

export function normalizeCronExpression(cronjob?: string): string {
  return !cronjob || cronjob === '' ? DEFAULT_CRON_EXPRESSION : cronjob
}

export function toSelectOptions(
  values: readonly (string | number)[],
): SelectionData<string>[] {
  return values.map((value) => ({ label: String(value), value: String(value) }))
}

export function getAmountKey(period: CronPeriod): CronAmountKey {
  return `${period}Amount`
}

export function parseNumericCustomValues(element: string): string[] {
  return element.split(',').map((num) => (+num).toString())
}

export function parseCustomValues(element: string): string[] {
  return element.split(',')
}

interface PeriodTokenRules {
  every: string
  secondaryMode: CronjobScheduleType
  secondaryToken: string
  defaultMode: CronjobScheduleType
  defaultToken: string
  parseAmount: (element: string) => string[]
}

// The one place period-specific cron syntax lives. Add a new period by adding a row here.
const PERIOD_RULES: Record<CronPeriod, PeriodTokenRules> = {
  minutes: {
    every: '*',
    secondaryMode: 'even',
    secondaryToken: '*/2',
    defaultMode: 'odd',
    defaultToken: '1-59/2',
    parseAmount: parseNumericCustomValues,
  },
  hours: {
    every: '*',
    secondaryMode: 'even',
    secondaryToken: '*/2',
    defaultMode: 'odd',
    defaultToken: '1-23/2',
    parseAmount: parseNumericCustomValues,
  },
  days: {
    every: '*',
    secondaryMode: 'even',
    secondaryToken: '*/2',
    defaultMode: 'odd',
    defaultToken: '1-31/2',
    parseAmount: parseNumericCustomValues,
  },
  months: {
    every: '*',
    secondaryMode: 'even',
    secondaryToken: '*/2',
    defaultMode: 'odd',
    defaultToken: '1-11/2',
    parseAmount: parseCustomValues,
  },
  weekdays: {
    every: '*',
    secondaryMode: 'working',
    secondaryToken: '1-5',
    defaultMode: 'holidays',
    defaultToken: '0,6',
    parseAmount: parseCustomValues,
  },
}

const PERIOD_ORDER: CronPeriod[] = [
  'minutes',
  'hours',
  'days',
  'months',
  'weekdays',
]

export function parseCronPart(
  period: CronPeriod,
  element: string,
  schedule: CronjobSchedule,
): void {
  const rules = PERIOD_RULES[period]
  const amountKey = getAmountKey(period)

  if (element === rules.every) {
    schedule[period] = 'every'
    schedule[amountKey] = []
  } else if (element === rules.secondaryToken) {
    schedule[period] = rules.secondaryMode
    schedule[amountKey] = []
  } else if (element === rules.defaultToken) {
    schedule[period] = rules.defaultMode
    schedule[amountKey] = []
  } else {
    schedule[period] = 'custom'
    schedule[amountKey] = rules.parseAmount(element)
  }
}

export function parseCronExpression(cronjob: string): CronjobSchedule {
  const schedule = createDefaultSchedule()

  cronjob.split(' ').forEach((element, index) => {
    const period = PERIOD_ORDER[index]
    if (period) parseCronPart(period, element, schedule)
  })

  return schedule
}

export function buildCronPart(
  period: CronPeriod,
  schedule: CronjobSchedule,
): string {
  const rules = PERIOD_RULES[period]
  const amountKey = getAmountKey(period)
  const amounts = schedule[amountKey]

  if (amounts.length) {
    schedule[period] = 'custom'
    return amounts.join(',')
  }

  const mode = schedule[period]
  if (mode === 'every') return rules.every
  if (mode === rules.secondaryMode) return rules.secondaryToken
  return rules.defaultToken
}

export function buildCronExpression(schedule: CronjobSchedule): string {
  return PERIOD_ORDER.map((period) => buildCronPart(period, schedule)).join(' ')
}
